import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  MapPin, 
  Users, 
  Clock, 
  ShieldCheck, 
  Music,
  Heart,
  Sparkles,
  ShoppingBag,
  Mail,
  Phone,
  ArrowLeft,
  CheckCircle2,
  Banknote
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import VendorForm from '../components/VendorForm';
import { 
  eventInfo, 
  heroImage, 
  highlights, 
  programLocations, 
  mainStageProgram,
  regionalProducts,
  wineStalls,
  contactInfo,
  vendorPricing,
  importantInfo
} from '../data/denzlinskehokrajeData';

const DenZlinskehokrajeLanding = () => {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);

  const scrollToForm = () => {
    setShowForm(true);
    setTimeout(() => {
      document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const iconComponents = {
    Music, Sparkles, ShoppingBag, Heart, Users, Clock, Calendar, ShieldCheck, CheckCircle2, Banknote
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 w-full bg-black z-50 border-b border-gray-800 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-gray-300 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Přehled akcí</span>
            </Button>
            <div className="font-bold text-xl text-white">Den Zlínského kraje</div>
          </div>
          <Button 
            onClick={scrollToForm}
            className="bg-white hover:bg-gray-100 text-black font-semibold px-8 py-2 rounded-full shadow-lg hover:shadow-xl transition-all"
          >
            PŘIHLÁSIT SE JAKO PRODEJCE
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={heroImage}
            alt="Den Zlínského kraje"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/85"></div>
        </div>
        
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 uppercase tracking-tight">
            {eventInfo.title}
          </h1>
          
          <p className="text-2xl md:text-4xl font-light mb-8 text-gray-300">
            {eventInfo.subtitle}
          </p>

          <div className="flex flex-col md:flex-row gap-6 items-center justify-center text-xl mb-12">
            <div className="flex items-center gap-2">
              <Calendar className="h-6 w-6 text-white" />
              <span className="font-bold">{eventInfo.date}</span>
            </div>
            <div className="hidden md:block h-6 w-px bg-white"></div>
            <div className="flex items-center gap-2">
              <MapPin className="h-6 w-6 text-white" />
              <span>{eventInfo.location}</span>
            </div>
          </div>

          <div className="inline-block bg-yellow-400 text-black px-8 py-3 rounded-full text-lg font-bold mb-12">
            {eventInfo.entryFee}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={scrollToForm}
              size="lg"
              className="bg-yellow-400 hover:bg-yellow-500 text-black px-12 py-6 text-xl rounded-full shadow-2xl font-bold"
            >
              Chci být prodejcem
            </Button>
            <a 
              href="https://www.denzk.cz" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-yellow-400 text-white hover:bg-yellow-400 hover:text-black px-12 py-6 text-xl rounded-full font-bold"
              >
                Zobrazit program
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Highlights Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4">
            Na co se můžete těšit?
          </h2>
          <p className="text-xl text-gray-600 text-center mb-16 max-w-3xl mx-auto">
            Celodenní program plný zábavy, hudby, regionálních produktů a aktivit pro celou rodinu
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {highlights.map((item, idx) => {
              const Icon = iconComponents[item.icon] || ShoppingBag;
              return (
                <Card key={idx} className="border-2 border-gray-200 hover:border-yellow-400 hover:shadow-xl transition-all">
                  <CardHeader>
                    <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mb-4">
                      <Icon className="h-8 w-8 text-black" />
                    </div>
                    <CardTitle className="text-2xl">{item.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4">
            Video z akce
          </h2>
          <p className="text-xl text-gray-600 text-center mb-12">
            Podívejte se na atmosféru z minulého ročníku
          </p>

          <div className="max-w-5xl mx-auto">
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute top-0 left-0 w-full h-full rounded-xl shadow-2xl border-4 border-yellow-400"
                src="https://www.youtube.com/embed/_Koy530oDL0"
                title="Den Zlínského kraje - Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Vendor Pricing Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-black text-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4">
            Podmínky pro prodejce
          </h2>
          <p className="text-xl text-center mb-16 text-gray-300">
            Staňte se součástí největší akce Zlínského kraje
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {/* Main Seller */}
            <Card className="border-4 border-yellow-400">
              <CardHeader className="bg-yellow-400 text-black">
                <CardTitle className="text-center text-2xl">{vendorPricing.mainSeller.title}</CardTitle>
                <CardDescription className="text-center text-black/80">
                  Stánek {vendorPricing.mainSeller.size}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-8 text-center">
                <div className="text-5xl font-bold mb-4 text-gray-900">
                  {vendorPricing.mainSeller.price.toLocaleString()} Kč
                </div>
                <p className="text-gray-600 mb-6">{vendorPricing.mainSeller.description}</p>
                <Button 
                  onClick={scrollToForm}
                  className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-bold"
                  size="lg"
                >
                  Přihlásit se
                </Button>
              </CardContent>
            </Card>

            {/* Other Seller */}
            <Card className="border-4 border-yellow-400">
              <CardHeader className="bg-white text-black border-b-2 border-yellow-400">
                <CardTitle className="text-center text-2xl">{vendorPricing.otherSeller.title}</CardTitle>
                <CardDescription className="text-center text-gray-700">
                  Stánek {vendorPricing.otherSeller.size}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-8 text-center">
                <div className="text-5xl font-bold mb-4 text-gray-900">
                  {vendorPricing.otherSeller.price.toLocaleString()} Kč
                </div>
                <p className="text-gray-600 mb-6">{vendorPricing.otherSeller.description}</p>
                <Button 
                  onClick={scrollToForm}
                  className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-bold"
                  size="lg"
                >
                  Přihlásit se
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Important Info */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-12">
            Důležité informace
          </h2>

          <div className="space-y-4">
            {importantInfo.map((info, idx) => (
              <div key={idx} className="flex items-start gap-3 bg-gray-50 p-4 rounded-lg border-2 border-gray-200 hover:border-black transition-all">
                <CheckCircle2 className="h-6 w-6 text-black mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">{info}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vendor Application Form */}
      {showForm && (
        <section id="application-form" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-4">
              Přihláška prodejce
            </h2>
            <p className="text-xl text-gray-600 text-center mb-12">
              Vyplňte formulář a staňte se součástí Dne Zlínského kraje 2026
            </p>
            
            <VendorForm eventId={eventInfo.eventId} eventName={eventInfo.title} />
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-black text-white py-12 px-4 sm:px-6 lg:px-8 border-t-4 border-yellow-400">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4 text-yellow-400">Den Zlínského kraje</h3>
              <p className="text-gray-400">{eventInfo.description}</p>
            </div>
            
            <div>
              <h3 className="text-xl font-bold mb-4">Kontakt</h3>
              <div className="space-y-2 text-gray-400">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  <a href={`mailto:${contactInfo.email}`} className="hover:text-yellow-400 transition-colors">
                    {contactInfo.email}
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  <span>{contactInfo.phone}</span>
                </div>
                <p className="text-sm mt-4">{contactInfo.address}</p>
              </div>
            </div>
            
            <div>
              <h3 className="text-xl font-bold mb-4">Sledujte nás</h3>
              <div className="flex gap-4">
                <a 
                  href={contactInfo.facebook} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-yellow-400 transition-colors"
                >
                  Facebook
                </a>
                <a 
                  href={contactInfo.instagram} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-yellow-400 transition-colors"
                >
                  Instagram
                </a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-500 text-sm">
            <p>© 2026 Zlínský kraj. Všechna práva vyhrazena.</p>
            <p className="mt-2">Změna programu vyhrazena. Na akci budou pořizovány foto a video záznamy pro propagaci.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DenZlinskehokrajeLanding;
